import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export interface DietEntry {
  id?: string;
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  servingSize?: string;
  mealType?: string;
}

interface MealSectionProps {
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  entries: DietEntry[];
  onAddEntry: (entry: DietEntry) => void;
  onRemoveEntry: (entryId: string) => void;
}

const mealMeta: Record<string, { label: string; emoji: string; color: string }> = {
  breakfast: { label: 'Breakfast', emoji: '🌅', color: 'bg-amber-50 border-amber-200' },
  lunch:     { label: 'Lunch',     emoji: '☀️',  color: 'bg-green-50 border-green-200' },
  dinner:    { label: 'Dinner',    emoji: '🌙',  color: 'bg-indigo-50 border-indigo-200' },
  snack:     { label: 'Snacks',    emoji: '🍎',  color: 'bg-rose-50 border-rose-200' },
};

const emptyFood = { name: '', calories: '', protein: '', carbs: '', fat: '', servingSize: '' };

export default function MealSection({ mealType, entries, onAddEntry, onRemoveEntry }: MealSectionProps) {
  const [open, setOpen] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyFood);

  const meta = mealMeta[mealType];
  const totalCal = entries.reduce((s, e) => s + (e.calories || 0), 0);

  const handleAdd = () => {
    if (!form.name.trim()) return;
    onAddEntry({
      id: Math.random().toString(36).slice(2),
      name: form.name.trim(),
      calories: parseFloat(form.calories) || 0,
      protein: parseFloat(form.protein) || 0,
      carbs: parseFloat(form.carbs) || 0,
      fat: parseFloat(form.fat) || 0,
      servingSize: form.servingSize || undefined,
      mealType,
    });
    setForm(emptyFood);
    setShowForm(false);
  };

  const inputClass = 'w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400';

  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${meta.color}`}>
      {/* Header */}
      <button
        className="w-full flex items-center justify-between p-4 hover:bg-black/5 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">{meta.emoji}</span>
          <span className="font-semibold text-gray-800">{meta.label}</span>
          {entries.length > 0 && (
            <span className="text-xs bg-white/70 text-gray-600 px-2 py-0.5 rounded-full">{entries.length} items</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {totalCal > 0 && <span className="text-sm font-medium text-gray-600">{Math.round(totalCal)} kcal</span>}
          {open ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="bg-white border-t border-gray-100 px-4 pb-4 pt-3">
          {/* Entries list */}
          {entries.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {entries.map((entry) => (
                <div key={entry.id || entry.name} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{entry.name}</p>
                    <p className="text-xs text-gray-400">
                      {entry.calories} kcal · P {entry.protein}g · C {entry.carbs}g · F {entry.fat}g
                      {entry.servingSize && ` · ${entry.servingSize}`}
                    </p>
                  </div>
                  <button
                    onClick={() => onRemoveEntry(entry.id || entry.name)}
                    className="ml-3 p-1 text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add food form */}
          {showForm ? (
            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              <input
                autoFocus
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                placeholder="Food name *"
                className={inputClass}
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  min={0}
                  value={form.calories}
                  onChange={e => setForm(p => ({ ...p, calories: e.target.value }))}
                  placeholder="Calories (kcal)"
                  className={inputClass}
                />
                <input
                  value={form.servingSize}
                  onChange={e => setForm(p => ({ ...p, servingSize: e.target.value }))}
                  placeholder="Serving size (optional)"
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  min={0}
                  value={form.protein}
                  onChange={e => setForm(p => ({ ...p, protein: e.target.value }))}
                  placeholder="Protein (g)"
                  className={inputClass}
                />
                <input
                  type="number"
                  min={0}
                  value={form.carbs}
                  onChange={e => setForm(p => ({ ...p, carbs: e.target.value }))}
                  placeholder="Carbs (g)"
                  className={inputClass}
                />
                <input
                  type="number"
                  min={0}
                  value={form.fat}
                  onChange={e => setForm(p => ({ ...p, fat: e.target.value }))}
                  placeholder="Fat (g)"
                  className={inputClass}
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleAdd}
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  Add Food
                </button>
                <button
                  onClick={() => { setShowForm(false); setForm(emptyFood); }}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add food
            </button>
          )}
        </div>
      )}
    </div>
  );
}
